"use client";
import React from "react";
import PropTypes from "prop-types";
import CustomImage from "../../../../../../components/atoms/customImage";

/**
 * Renders a horizontal free-flow draggable carousel of product cards.
 * Works on both desktop (mouse drag) and mobile (touch scroll).
 * @param {object} props - Component props
 * @param {Array} props.products - List of product objects to display
 * @param {boolean} props.isLoading - Whether products are loading
 * @returns {React.ReactElement|null} Rendered carousel or null
 */
const ProductCarousel = ({ products, isLoading = false }) => {
  const touchStartX = React.useRef(null);

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="p-10 pr-0">
          <div
            className="flex animate-pulse items-center gap-2 rounded-xl p-10 shadow-lg"
            style={{ backgroundColor: "#ffffff" }}
          >
            <div className="mr-10 rounded-lg bg-gray-200" style={{ width: "50px", height: "50px", flexShrink: 0 }} />
            <div className="min-w-0 flex-1">
              <div className="mb-2 h-3 w-3/4 rounded bg-gray-200" />
              <div className="h-3 w-1/2 rounded bg-gray-200" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (products.length === 0) return null;

  /**
   * Stops touch/wheel events from bubbling up to parent video swipe handlers.
   * @param {React.SyntheticEvent} e - The event object
   */
  const onStopBubble = (e) => e.stopPropagation();

  return (
    <div
      className="flex w-full cursor-grab select-none gap-[6px] overflow-x-auto px-[6px] py-[6px] scrollbar-none"
      onTouchStart={onStopBubble}
      onTouchMove={onStopBubble}
      onWheel={onStopBubble}
    >
      {products.map((product) => {
        /** Opens the product URL in a new tab. */
        const openProduct = () => {
          if (product.productUrl) {
            window.open(product.productUrl, "_blank", "noopener,noreferrer");
          }
        };

        /** Handles click navigation on desktop, stopping parent propagation. */
        const onProductClick = (e) => {
          e.stopPropagation();
          openProduct();
        };

        /** Records touch start X position and stops propagation to parent swipe handler. */
        const onProductTouchStart = (e) => {
          touchStartX.current = e.touches[0].clientX;
          e.stopPropagation();
        };

        /** Navigates on tap (no significant horizontal movement) without relying on synthetic click. */
        const onProductTouchEnd = (e) => {
          const diff = Math.abs(e.changedTouches[0].clientX - (touchStartX.current ?? e.changedTouches[0].clientX));
          e.stopPropagation();
          if (diff < 10) {
            e.preventDefault(); // suppress the synthetic click that would double-fire openProduct
            openProduct();
          }
          touchStartX.current = null;
        };

        return (
          <div
            key={product.productId || product.id}
            className={`${products.length === 1 ? "w-full" : "min-w-[85%]"} flex cursor-pointer items-center gap-2 overflow-hidden rounded-xl bg-white-900 p-10 shadow-lg`}
            onClick={onProductClick}
            onTouchStart={onProductTouchStart}
            onTouchEnd={onProductTouchEnd}
          >
            <div className="relative mr-10 h-[50px] w-[50px] flex-shrink-0 overflow-hidden rounded-lg">
              <CustomImage
                src={product.productImage?.path || product.image?.path}
                alt={product.productName || product.name || "Product"}
                fill={true}
                className="object-cover"
                sizes="50px"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-14 font-medium text-gray-900">
                {product.productName || product.name || "Product Name"}
              </p>
              <p className="text-xs font-semibold text-gray-700">
                ₹{Math.floor(product.price?.listPrice || product.price || 999)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

ProductCarousel.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      id: PropTypes.string,
      productName: PropTypes.string,
      name: PropTypes.string,
      productImage: PropTypes.shape({ path: PropTypes.string }),
      image: PropTypes.shape({ path: PropTypes.string }),
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({ listPrice: PropTypes.number })]),
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
};

export default ProductCarousel;
